import {FC, useRef} from 'react';
import {api, EOAuthType, IIntegrationAccount, OAUTH_ICONS, useT} from '@kcast-fe/core';
import {Box, Flex, Grid, Center, useBreakpointValue} from '@chakra-ui/react';
import {Checkbox, MenuButton, QuickMenu, Svg} from 'ui-kit';

interface IProps {
  service: EOAuthType;
  account: IIntegrationAccount;
}

export const AccountItem: FC<IProps> = ({account, service}) => {
  const disconnectMutation = api.oauth.useDisconnectMutation();
  const setDefaultMutation = api.oauth.useSetDefaultMutation();

  const {platform_unique_id, is_primary, display_name} = account;

  const isSmToMd = useBreakpointValue({base: false, sm: true, md: false});

  const ref = useRef<HTMLDivElement | null>(null);
  const {t} = useT();

  const handleDisconnect = async () => {
    disconnectMutation.mutate({
      oauthType: service,
      platform_unique_id: platform_unique_id
    });
  };

  const handleSetDefault = async () => {
    setDefaultMutation.mutate({
      oauthType: service,
      platform_unique_id: platform_unique_id
    });
  };

  return (
    <Box
      ref={ref}
      p={{base: '8px'}}
      borderBottom={'thin solid'}
      borderColor={'{colors.borderDefault}'}
      _last={{borderColor: 'transparent'}}
    >
      <Grid gap={{base: '16px'}} gridTemplateColumns={'24px 1fr 60px auto'} alignItems={'center'}>
        <Center h={'100%'}>
          <Svg size={'s24'} name={OAUTH_ICONS[service]} />
        </Center>

        <Flex flexDirection={'column'} gap={'2px'} overflow={'hidden'}>
          <Box className={'cut-text'} color={'{colors.textPrimary}'}>
            {t(`enums.oauthType.${service}`)} {display_name ?? t('common.unknown')}
          </Box>
        </Flex>

        <Center>
          <Checkbox
            size={'lg'}
            variant={'solid'}
            checked={is_primary}
            key={`${is_primary}`}
            onChange={!is_primary ? handleSetDefault : undefined}
          />
        </Center>

        {isSmToMd ? (
          <QuickMenu
            hideActive
            container={ref}
            trigger={<Svg name={'Dots'} size={'s24'} />}
            items={[{id: '0', name: t('actions.disconnect'), onClick: handleDisconnect}]}
          />
        ) : (
          <MenuButton
            container={ref}
            variant={'success'}
            size={'sm'}
            disabled={disconnectMutation.isPending}
            label={t('labels.connected')}
            items={[{label: t('actions.disconnect'), onClick: handleDisconnect}]}
          />
        )}
      </Grid>
    </Box>
  );
};
